import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function BuyerForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: email, 2: OTP, 3: new password
  const [formData, setFormData] = useState({
    email: '',
    otp: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (step === 1) {
        // Send OTP
        const response = await fetch("https://didrouteapi.com/api/buyers/forgot-password", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: formData.email }),
        });

        const data = await response.json();

        if (response.ok) {
          setStep(2);
        } else {
          setError(data.error || 'Failed to send OTP');
        }
      } else if (step === 2) {
        // Verify OTP and proceed to password reset
        setStep(3);
      } else if (step === 3) {
        // Validate passwords
        if (formData.newPassword !== formData.confirmPassword) {
          setError("Passwords don't match");
          return;
        }

        // Reset password
        const response = await fetch("https://didrouteapi.com/api/buyers/reset-password", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: formData.email,
            otp: formData.otp,
            newPassword: formData.newPassword
          }),
        });

        const data = await response.json();

        if (response.ok) {
          navigate('/buyer/login');
        } else {
          setError(data.error || 'Failed to reset password');
        }
      }
    } catch (error) {
      setError('Server error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-[-10px]">
      <div className="flex items-center justify-center min-h-screen bg-cover bg-center relative"
        style={{
          backgroundImage: "url(https://images.pexels.com/photos/2150/sky-space-dark-galaxy.jpg?auto=compress&cs=tinysrgb&w=1600)",
        }}>
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>
        <div className="relative z-10 bg-white bg-opacity-5 backdrop-blur-md shadow-lg rounded-lg max-w-md w-full px-6 py-8 sm:px-10 sm:py-12">
          <h2 className="text-center text-2xl font-bold leading-9 tracking-tight text-white">
            {step === 1 ? 'Forgot Password' : step === 2 ? 'Enter OTP' : 'Reset Password'}
          </h2>

          {error && (
            <div className="mt-4 text-red-500 text-sm text-center">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6 mt-8">
            {step === 1 && (
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md bg-gray-800 bg-opacity-30 px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter your email"
                />
              </div>
            )}

            {step === 2 && (
              <div>
                <label htmlFor="otp" className="block text-sm font-medium text-gray-300">
                  Enter OTP
                </label>
                <input
                  id="otp"
                  name="otp"
                  type="text"
                  required
                  value={formData.otp}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md bg-gray-800 bg-opacity-30 px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter OTP sent to your email"
                />
              </div>
            )}

            {step === 3 && (
              <>
                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-300">
                    New Password
                  </label>
                  <input
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    required
                    value={formData.newPassword}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md bg-gray-800 bg-opacity-30 px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter new password"
                  />
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300">
                    Confirm Password
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md bg-gray-800 bg-opacity-30 px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Confirm new password"
                  />
                </div>
              </>
            )}

            <div>
              <button
                type="submit"
                disabled={loading}
                className={`w-full rounded-md px-4 py-2 text-sm font-medium text-white shadow-sm transition-transform duration-200 ease-in-out transform hover:scale-105
                  ${loading 
                    ? 'bg-gray-500 cursor-not-allowed' 
                    : 'bg-indigo-500 hover:bg-indigo-400'}`}
              >
                {loading ? 'Processing...' : 
                  step === 1 ? 'Send OTP' : 
                  step === 2 ? 'Verify OTP' : 
                  'Reset Password'}
              </button>
            </div>
          </form>

          <p className="mt-6 text-center text-sm text-gray-400">
            Remember your password?{" "}
            <a href="/buyer/login" className="font-semibold text-indigo-400 hover:text-indigo-300">
              Login here
            </a>
          </p>
        </div>
      </div>
    </div>
  );
} 